import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Cross2Icon } from '@radix-ui/react-icons';
import { Spinner, Toggle } from '../../../components';
import { planService } from '../../../services/planService';
import { PlanCurrencyCode } from '../../../types/api';
import {
  CUSTOM_PLAN_NAME,
  EditSubscriptionFormData,
  PRICING_GRID_CURRENCIES,
  PlanFeatureItem,
  PricingRow,
} from '../types/subscription';
import {
  buildEditFormFromPlanDetail,
  buildFeaturesFromVariantMeta,
  updateVariantFeatureFlag,
} from '../subscriptionData';

interface EditSubscriptionModalProps {
  isOpen: boolean;
  planId: string | null;
  onClose: () => void;
  onSave: (planId: string, data: EditSubscriptionFormData) => Promise<void>;
  isSaving?: boolean;
}

const CURRENCY_LABELS: Record<PlanCurrencyCode, string> = {
  INR: 'INR (₹)',
  USD: 'USD ($)',
  CAD: 'CAD ($)',
  EUR: 'EUR (€)',
};

const updatePricingAmount = (
  grid: PricingRow[],
  planDetailId: number,
  currency: PlanCurrencyCode,
  amount: number
): PricingRow[] =>
  grid.map((row) =>
    row.planDetailId === planDetailId
      ? {
          ...row,
          [currency]: {
            ...row[currency],
            amount,
          },
        }
      : row
  );

export const EditSubscriptionModal: React.FC<EditSubscriptionModalProps> = ({
  isOpen,
  planId,
  onClose,
  onSave,
  isSaving = false,
}) => {
  const [formData, setFormData] = useState<EditSubscriptionFormData | null>(null);
  const [selectedPlanDetailId, setSelectedPlanDetailId] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen || !planId) {
      setFormData(null);
      setSelectedPlanDetailId(null);
      setLoadError(null);
      return;
    }

    let cancelled = false;

    const fetchPlan = async () => {
      setIsLoading(true);
      setLoadError(null);
      setFormData(null);
      setSelectedPlanDetailId(null);

      try {
        const planDetail = await planService.getPlanById(planId);
        if (cancelled) return;
        const nextFormData = buildEditFormFromPlanDetail(planDetail);
        setFormData(nextFormData);
        setSelectedPlanDetailId(nextFormData.pricingGrid[0]?.planDetailId ?? null);
      } catch (error) {
        if (cancelled) return;
        setLoadError(error instanceof Error ? error.message : 'Failed to load plan details');
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    fetchPlan();

    return () => {
      cancelled = true;
    };
  }, [isOpen, planId]);

  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isSaving && !isLoading) onClose();
    };

    document.addEventListener('keydown', handleEscape);
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [isOpen, isSaving, isLoading, onClose]);

  const selectedPricingRow = useMemo(
    () =>
      formData?.pricingGrid.find((row) => row.planDetailId === selectedPlanDetailId) ?? null,
    [formData, selectedPlanDetailId]
  );

  const selectedVariant = useMemo(
    () =>
      formData?.variants.find((variant) => variant.planDetailId === selectedPlanDetailId) ?? null,
    [formData, selectedPlanDetailId]
  );

  const selectedFeatures = useMemo(
    () => (selectedVariant ? buildFeaturesFromVariantMeta(selectedVariant) : []),
    [selectedVariant]
  );

  const selectedDurationLabel = selectedPricingRow?.label ?? '';

  const handleChange = useCallback(
    <K extends keyof EditSubscriptionFormData>(
      field: K,
      value: EditSubscriptionFormData[K]
    ) => {
      setFormData((prev) => (prev ? { ...prev, [field]: value } : prev));
    },
    []
  );

  const handlePricingChange = useCallback(
    (planDetailId: number, currency: PlanCurrencyCode, amount: number) => {
      setFormData((prev) =>
        prev
          ? {
              ...prev,
              pricingGrid: updatePricingAmount(prev.pricingGrid, planDetailId, currency, amount),
            }
          : prev
      );
    },
    []
  );

  const handleVariantUpdate = useCallback(
    (planDetailId: number, updater: (variant: EditSubscriptionFormData['variants'][number]) => EditSubscriptionFormData['variants'][number]) => {
      setFormData((prev) =>
        prev
          ? {
              ...prev,
              variants: prev.variants.map((variant) =>
                variant.planDetailId === planDetailId ? updater(variant) : variant
              ),
            }
          : prev
      );
    },
    []
  );

  const handleFeatureToggle = useCallback(
    (featureKey: string, enabled: boolean) => {
      if (selectedPlanDetailId == null) return;
      handleVariantUpdate(selectedPlanDetailId, (variant) =>
        updateVariantFeatureFlag(variant, featureKey, enabled)
      );
    },
    [handleVariantUpdate, selectedPlanDetailId]
  );

  const handleConnectsChange = useCallback(
    (value: number) => {
      if (selectedPlanDetailId == null) return;
      handleVariantUpdate(selectedPlanDetailId, (variant) => ({
        ...variant,
        connects: Math.max(0, value),
      }));
    },
    [handleVariantUpdate, selectedPlanDetailId]
  );

  const isDynamicPlan = Boolean(formData?.isDynamic);

  const handleSubmit = useCallback(async () => {
    if (!planId || !formData) return;
    await onSave(planId, formData);
  }, [planId, formData, onSave]);

  if (!isOpen || !planId) return null;

  return (
    <div className="subscription-modal" role="presentation">
      <div
        className="subscription-modal__overlay"
        onClick={isSaving || isLoading ? undefined : onClose}
        aria-hidden="true"
      />

      <div
        className="subscription-modal__dialog subscription-modal__dialog--edit"
        role="dialog"
        aria-modal="true"
        aria-labelledby="edit-subscription-title"
      >
        <header className="subscription-modal__header">
          <h2 id="edit-subscription-title">Edit Subscription Plan</h2>
          <button
            type="button"
            className="subscription-modal__close"
            onClick={onClose}
            disabled={isSaving || isLoading}
            aria-label="Close"
          >
            <Cross2Icon />
          </button>
        </header>

        {isLoading ? (
          <div className="subscription-modal__loading">
            <Spinner size="medium" />
            <p>Loading plan details...</p>
          </div>
        ) : loadError ? (
          <div className="subscription-modal__error">
            <p>{loadError}</p>
            <button
              type="button"
              className="subscription-modal__btn subscription-modal__btn--ghost"
              onClick={onClose}
            >
              Close
            </button>
          </div>
        ) : formData ? (
          <>
            <div className="subscription-modal__body subscription-modal__body--edit">
              <div className="subscription-modal__field">
                <label htmlFor="plan-name">Plan Name</label>
                <input
                  id="plan-name"
                  type="text"
                  className="subscription-modal__text-input"
                  value={isDynamicPlan ? CUSTOM_PLAN_NAME : formData.name}
                  onChange={(e) => {
                    if (isDynamicPlan) return;
                    handleChange('name', e.target.value);
                  }}
                  disabled={isDynamicPlan}
                  readOnly={isDynamicPlan}
                />
              </div>

              <div className="subscription-modal__field">
                <label htmlFor="plan-description">Plan Description</label>
                <textarea
                  id="plan-description"
                  className="subscription-modal__textarea"
                  value={formData.description}
                  onChange={(e) => handleChange('description', e.target.value)}
                  rows={3}
                />
              </div>

              <div className="subscription-modal__toggles">
                <Toggle
                  label="Popular Badge"
                  checked={formData.isPopular}
                  onChange={(checked) => handleChange('isPopular', checked)}
                  className="subscription-modal__toggle"
                />
                <Toggle
                  label="Plan Status (Active)"
                  checked={isDynamicPlan ? false : formData.isActive}
                  onChange={(checked) => {
                    if (isDynamicPlan) return;
                    handleChange('isActive', checked);
                  }}
                  disabled={isDynamicPlan}
                  className="subscription-modal__toggle"
                />
              </div>
              {isDynamicPlan && (
                <p className="subscription-modal__field">
                  Saving does not change users who already have this plan assigned. The next assignment uses the new values.
                </p>
              )}

              {formData.pricingGrid.length > 0 && (
                <div className="subscription-modal__duration-section">
                  <label id="billing-duration-label">Billing Duration</label>
                  <div
                    className="subscription-modal__duration-buttons"
                    role="group"
                    aria-labelledby="billing-duration-label"
                  >
                    {formData.pricingGrid.map((row) => (
                      <button
                        key={row.planDetailId}
                        type="button"
                        className={`subscription-modal__duration-btn ${
                          selectedPlanDetailId === row.planDetailId
                            ? 'subscription-modal__duration-btn--active'
                            : ''
                        }`}
                        aria-pressed={selectedPlanDetailId === row.planDetailId}
                        onClick={() => setSelectedPlanDetailId(row.planDetailId)}
                      >
                        {row.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {selectedPricingRow && (
                <section
                  className="subscription-modal__pricing-section"
                  aria-labelledby="regional-pricing-heading"
                >
                  <h3 id="regional-pricing-heading" className="subscription-modal__section-heading">
                    Regional Pricing
                    <span className="subscription-modal__section-heading-suffix">
                      — {selectedDurationLabel}
                    </span>
                  </h3>
                  <div className="subscription-modal__currency-grid">
                    {PRICING_GRID_CURRENCIES.map((currency) => (
                      <div key={currency} className="subscription-modal__currency-field">
                        <label htmlFor={`price-${currency}`}>{CURRENCY_LABELS[currency]}</label>
                        <input
                          id={`price-${currency}`}
                          type="number"
                          min="0"
                          className="subscription-modal__text-input"
                          value={selectedPricingRow[currency].amount}
                          onChange={(e) =>
                            handlePricingChange(
                              selectedPricingRow.planDetailId,
                              currency,
                              Number(e.target.value)
                            )
                          }
                          aria-label={`${selectedDurationLabel} ${currency} price`}
                        />
                      </div>
                    ))}
                  </div>
                </section>
              )}

              {selectedVariant && (
                <section
                  className="subscription-modal__features-section"
                  aria-labelledby="included-features-heading"
                >
                  <h3 id="included-features-heading" className="subscription-modal__section-heading">
                    Included Features
                    <span className="subscription-modal__section-heading-suffix">
                      — {selectedDurationLabel}
                    </span>
                  </h3>

                  <div className="subscription-modal__connects-field">
                    <label htmlFor="plan-connects">Connects</label>
                    <input
                      id="plan-connects"
                      type="number"
                      min="0"
                      className="subscription-modal__text-input"
                      value={selectedVariant.connects}
                      onChange={(e) => handleConnectsChange(Number(e.target.value) || 0)}
                    />
                  </div>

                  <ul className="subscription-modal__feature-rows">
                    {selectedFeatures
                      .filter((feature) => feature.key !== 'connects')
                      .map((feature: PlanFeatureItem) => (
                        <li key={feature.id} className="subscription-modal__feature-row">
                          <span className="subscription-modal__feature-label">{feature.label}</span>
                          <Toggle
                            checked={feature.enabled}
                            onChange={(checked) => handleFeatureToggle(feature.key, checked)}
                            className="subscription-modal__feature-toggle"
                          />
                        </li>
                      ))}
                  </ul>
                </section>
              )}
            </div>

            <footer className="subscription-modal__footer">
              <button
                type="button"
                className="subscription-modal__btn subscription-modal__btn--ghost"
                onClick={onClose}
                disabled={isSaving}
              >
                Cancel
              </button>
              <button
                type="button"
                className="subscription-modal__btn subscription-modal__btn--primary"
                onClick={handleSubmit}
                disabled={isSaving}
              >
                {isSaving ? <Spinner size="small" /> : null}
                {isSaving ? 'Saving...' : 'Save Changes'}
              </button>
            </footer>
          </>
        ) : null}
      </div>
    </div>
  );
};
